import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { Card, LoadingState } from '@leanin/ui';
import { EntryJourneyScreen } from '@/components/entry-journeys';
import { AboutYouForm } from '@/components/onboarding/AboutYouForm';
import {
  clearPendingProfile,
  readPendingProfile,
  type PendingProfile,
} from '@/components/onboarding/pending-profile';
import { useFinishOnboarding } from '@/components/onboarding/finish-onboarding';
import { useUpdateMyPrivacy, useUpdateMyProfile } from '@/lib/mutations/identity';
import { useMe } from '@/lib/queries/identity';
import { trackOnboardingStepCompleted, trackOnboardingStepViewed } from '@/lib/analytics/events';
import { journeyStepParams } from '@/lib/analytics/onboarding-steps';
import { useTrackOnce } from '@/lib/analytics/use-track-once';
import { readEntryJourney } from '@/lib/entry-journey';

// Ad-journey onboarding — the design's minimal "About you" card. Saves
// through the same PATCH /members/me mutation as the organic ProfileStep,
// with the same GDPR marketing opt-in semantics; a direct hit with no
// journey in sessionStorage falls back to the organic flow.
//
// The join-network /get-started wizard collects these answers BEFORE signup
// and buffers them (see pending-profile). When that buffer is present this
// page submits it automatically and finishes onboarding without re-showing
// the form; otherwise (organic, or a Google signup whose OAuth round-trip
// cleared the buffer) it renders the form as usual.
export const Route = createFileRoute('/onboarding/j/about')({
  beforeLoad: () => {
    const journey = readEntryJourney();
    if (journey === null) {
      throw redirect({ to: '/onboarding/profile' });
    }
    return { journey };
  },
  component: JourneyAboutPage,
});

function JourneyAboutPage() {
  const t = useTranslations('EntryJourneys');
  const navigate = useNavigate();
  const { journey } = Route.useRouteContext();
  const updateProfile = useUpdateMyProfile();
  const updatePrivacy = useUpdateMyPrivacy();
  // The join journeys have no completion screen — this step's Continue
  // finishes onboarding directly and the hook lands on the relevant
  // directory (Circles for join-circle, Networks for join-network).
  const finishOnboarding = useFinishOnboarding(journey === 'join-network' ? 'li' : 'cm');
  // Prefill from the member row (OAuth signups already carry a name); same
  // swallow-errors-to-defaults contract as /onboarding/profile.
  const { data: me, isPending, isError } = useMe();
  const profile = me !== undefined && !isError ? me : undefined;
  const [error, setError] = useState<string | null>(null);

  // Pre-signup buffer from the /get-started wizard. Read once at mount; when
  // present we auto-submit and skip the form, unless that submit fails (then
  // we fall back to the form, prefilled with the buffered answers).
  const [buffered] = useState(readPendingProfile);
  const [autoApplyFailed, setAutoApplyFailed] = useState(false);
  const autoAppliedRef = useRef(false);
  // The continuation below runs after an await, so it can outlive this
  // page: mutateAsync settles regardless of the observer's lifecycle (that
  // is the point of using it), which also means a visitor who navigated
  // away mid-PATCH would otherwise be yanked back by the trailing
  // navigate()/finishOnboarding() — the router singleton is not mount-gated.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // mutateAsync, not mutate-with-callbacks: per-call `mutate` callbacks are
  // delivered through the hook's mutation observer, and when the observer
  // recycles while the request is in flight they are silently dropped. The
  // auto-drain fires this from a mount effect, where StrictMode's simulated
  // remount recycles the observer every time — the profile PATCH landed but
  // the whole continuation (consent, analytics, finish, redirect) never ran,
  // stranding the visitor on the spinner with their marketing opt-in lost.
  // The awaited promise settles regardless of observer lifecycle.
  const applyProfile = useCallback(
    async (values: PendingProfile, opts?: { fromBuffer?: boolean }) => {
      setError(null);
      try {
        await updateProfile.mutateAsync({
          firstName: values.firstName,
          lastName: values.lastName,
          jobTitle: values.jobTitle,
          industry: values.industry,
          place: values.place,
        });
      } catch {
        if (!mountedRef.current) return;
        setError(t('onboarding.about.saveError'));
        // A failed auto-apply must not strand the visitor on a spinner —
        // drop to the form so they can retry.
        if (opts?.fromBuffer === true) setAutoApplyFailed(true);
        return;
      }
      try {
        // Fire the privacy update only when the user opted in — the server
        // appends a `consent_events` row (purpose='marketing', GDPR Art. 7(1)
        // demonstrability). Non-blocking, and fired regardless of mount
        // state: the consent was given and must not be lost to a back press.
        if (values.marketingOptIn) {
          updatePrivacy.mutate({ marketingOptIn: true });
        }
        trackOnboardingStepCompleted(journeyStepParams(journey, '/onboarding/j/about'));
      } catch {
        // Analytics/privacy dispatch must never block the redirect below —
        // the profile save already committed.
      }
      // The user left the page mid-save: don't hijack wherever they went.
      // Onboarding stays incomplete and resumes on their next visit.
      if (!mountedRef.current) return;
      // start-circle continues into circle setup; the join journeys are
      // done — finish and land on their directory; individual sees the
      // completion step.
      if (journey === 'join-circle' || journey === 'join-network') {
        void finishOnboarding();
        return;
      }
      void navigate({
        to: journey === 'start-circle' ? '/onboarding/j/circle' : '/onboarding/j/youre-in',
      });
    },
    [journey, updateProfile, updatePrivacy, finishOnboarding, navigate, t],
  );

  // Drain the pre-signup buffer exactly once.
  useEffect(() => {
    if (buffered === null || autoAppliedRef.current) return;
    autoAppliedRef.current = true;
    clearPendingProfile();
    void applyProfile(buffered, { fromBuffer: true });
  }, [buffered, applyProfile]);

  // Only count a "viewed" when the form is actually shown — an auto-applied
  // buffer skips the form and goes straight to the completed event.
  useTrackOnce(
    () => trackOnboardingStepViewed(journeyStepParams(journey, '/onboarding/j/about')),
    buffered === null || autoApplyFailed,
  );

  const showAutoApplyLoader = buffered !== null && !autoApplyFailed;

  return (
    <EntryJourneyScreen journey={journey} current="about-you">
      <Card style={{ width: '100%', maxWidth: 440 }}>
        {showAutoApplyLoader || isPending ? (
          <LoadingState placement="inset" label={t('steps.aboutYou')} />
        ) : (
          <AboutYouForm
            initial={
              buffered ?? {
                firstName: profile?.firstName ?? '',
                lastName: profile?.lastName ?? '',
                jobTitle: profile?.jobTitle ?? null,
                industry: profile?.industry ?? null,
              }
            }
            submitLabel={t('onboarding.about.cta')}
            submitLoading={updateProfile.isPending}
            error={error}
            onSubmit={(values) => void applyProfile(values)}
          />
        )}
      </Card>
    </EntryJourneyScreen>
  );
}
